import { useEffect, useState } from "react";
import { api, formatError, formatDate } from "../lib/api";
import { PageHeader, Modal } from "../components/ui-kit";
import { Plus, Trash2, Pencil, Send, Undo2, History, Wrench, Printer } from "lucide-react";
import { useAuth } from "../lib/auth";
import SignaturePad from "../components/SignaturePad";
import PhotoUploader from "../components/PhotoUploader";
import { openReceiptPrint } from "../lib/receipt";
const fdt = formatDate;

const STATUS = {
  estoque: { label: "Em Estoque", cls: "badge-open" },
  com_tecnico: { label: "Com Técnico", cls: "badge-progress" },
  manutencao: { label: "Manutenção", cls: "badge-cancel" },
};

const EMPTY = { name: "", serial_number: "", brand: "", category: "", description: "" };

export default function ToolsPage({ selfOnly = false }) {
  const { user } = useAuth();
  const canManage = ["admin", "gestor"].includes(user?.role);
  const [tools, setTools] = useState([]);
  const [techs, setTechs] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [techFilter, setTechFilter] = useState("");

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [err, setErr] = useState("");

  const [assignOpen, setAssignOpen] = useState(false);
  const [assignForm, setAssignForm] = useState({ tool: null, technician_id: "", notes: "", signature: null, photos: [] });
  const [assignErr, setAssignErr] = useState("");

  const [returnOpen, setReturnOpen] = useState(false);
  const [returnForm, setReturnForm] = useState({ tool: null, signature: null, photos: [], notes: "" });
  const [returnErr, setReturnErr] = useState("");

  const [histOpen, setHistOpen] = useState(false);
  const [histItems, setHistItems] = useState([]);
  const [histTool, setHistTool] = useState(null);

  const load = () => {
    const params = new URLSearchParams();
    if (!selfOnly) {
      if (statusFilter) params.append("status", statusFilter);
      if (techFilter) params.append("technician_id", techFilter);
    }
    api.get(`/tools?${params.toString()}`).then((r) => setTools(r.data));
    if (canManage && !selfOnly) api.get("/users").then((r) => setTechs(r.data.filter((u) => u.role === "tecnico")));
  };
  useEffect(() => { load(); }, [statusFilter, techFilter, selfOnly]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setErr(""); setOpen(true); };
  const openEdit = (t) => { setEditing(t.id); setForm({ ...EMPTY, ...t }); setErr(""); setOpen(true); };
  const save = async () => {
    setErr("");
    try {
      if (editing) await api.put(`/tools/${editing}`, form);
      else await api.post("/tools", form);
      setOpen(false); load();
    } catch (e) { setErr(formatError(e)); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Excluir ${t.name} (SN ${t.serial_number})?`)) return;
    try { await api.delete(`/tools/${t.id}`); load(); }
    catch (e) { alert(formatError(e)); }
  };

  const openAssign = (t) => {
    setAssignForm({ tool: t, technician_id: "", notes: "", signature: null, photos: [] });
    setAssignErr(""); setAssignOpen(true);
  };
  const doAssign = async () => {
    setAssignErr("");
    if (!assignForm.signature) { setAssignErr("A assinatura do técnico é obrigatória"); return; }
    try {
      await api.post(`/tools/${assignForm.tool.id}/assign`, {
        technician_id: assignForm.technician_id,
        notes: assignForm.notes,
        signature: assignForm.signature,
        photos: assignForm.photos,
      });
      setAssignOpen(false); load();
    } catch (e) { setAssignErr(formatError(e)); }
  };

  const openReturn = (t) => {
    setReturnForm({ tool: t, signature: null, photos: [], notes: "" });
    setReturnErr(""); setReturnOpen(true);
  };
  const doReturn = async () => {
    setReturnErr("");
    if (!returnForm.signature) { setReturnErr("Assine para confirmar a devolução"); return; }
    try {
      await api.post(`/tools/${returnForm.tool.id}/return`, {
        signature: returnForm.signature,
        photos: returnForm.photos,
        notes: returnForm.notes,
      });
      setReturnOpen(false); load();
    } catch (e) { setReturnErr(formatError(e)); }
  };

  const returnTool = (t) => openReturn(t);

  const printReceipt = (t, mode = "retirada") => {
    openReceiptPrint({
      title: mode === "retirada" ? "Recibo de Retirada · Ferramenta" : "Recibo de Devolução · Ferramenta",
      subtitle: `${t.name}${t.brand ? ` · ${t.brand}` : ""}`,
      item: t.name,
      sn: t.serial_number,
      technician: t.technician_name || "-",
      date: t.assigned_at ? fdt(t.assigned_at) : "-",
      signature: t.signature,
      photos: t.photos || [],
      meta: [
        { label: "Categoria", value: t.category || "-" },
        { label: "Observações", value: t.notes || "-" },
      ],
    });
  };

  const printHistoryReceipt = (t, h, mode) => {
    const isReturn = mode === "devolucao";
    openReceiptPrint({
      title: isReturn ? "Recibo de Devolução · Ferramenta" : "Recibo de Retirada · Ferramenta",
      subtitle: t.name,
      item: t.name,
      sn: t.serial_number,
      technician: h.technician_name || "-",
      date: isReturn ? (h.returned_at ? fdt(h.returned_at) : "-") : (h.assigned_at ? fdt(h.assigned_at) : "-"),
      signature: isReturn ? h.return_signature : h.pickup_signature,
      photos: isReturn ? (h.return_photos || []) : (h.pickup_photos || []),
      meta: [
        { label: "Observações", value: (isReturn ? h.return_notes : h.notes) || "-" },
        { label: "Retirada em", value: h.assigned_at ? fdt(h.assigned_at) : "-" },
        { label: "Devolvida em", value: h.returned_at ? fdt(h.returned_at) : "-" },
      ],
    });
  };

  const openHistory = async (t) => {
    setHistTool(t);
    const { data } = await api.get(`/tools/${t.id}/history`);
    setHistItems(data);
    setHistOpen(true);
  };

  const filtered = tools.filter((t) =>
    !q || t.name.toLowerCase().includes(q.toLowerCase()) ||
    t.serial_number.toLowerCase().includes(q.toLowerCase()) ||
    (t.brand || "").toLowerCase().includes(q.toLowerCase())
  );

  if (selfOnly) {
    return (
      <div data-testid="my-tools-page">
        <PageHeader eyebrow="Ferramentas" title="Minhas Ferramentas">
          Ferramentas que estão sob sua responsabilidade
        </PageHeader>
        <div className="card">
          <table className="data">
            <thead>
              <tr><th>Ferramenta</th><th>SN</th><th>Marca</th><th>Recebida em</th><th>Obs</th></tr>
            </thead>
            <tbody>
              {filtered.map((t) => (
                <tr key={t.id} data-testid={`my-tool-${t.serial_number}`}>
                  <td>{t.name}</td>
                  <td className="mono font-semibold">{t.serial_number}</td>
                  <td>{t.brand || "-"}</td>
                  <td>{formatDate(t.assigned_at)}</td>
                  <td className="text-xs" style={{ color: "var(--text-dim)" }}>{t.notes || "-"}</td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-sm" style={{ color: "var(--text-mute)" }}>Nenhuma ferramenta com você no momento.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="tools-page">
      <PageHeader
        eyebrow="Inventário"
        title="Ferramentas"
        actions={canManage && (
          <button className="btn btn-primary" onClick={openNew} data-testid="new-tool-btn"><Plus size={16} /> Nova Ferramenta</button>
        )}
      >
        Cada ferramenta tem seu número de série próprio. Veja quem está com ela e desde quando.
      </PageHeader>

      <div className="card mb-4 p-4 flex gap-3 flex-wrap">
        <input className="input flex-1 min-w-[220px]" placeholder="Buscar por nome, SN ou marca..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="tools-search" />
        <select className="select w-48" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="tools-status-filter">
          <option value="">Todos status</option>
          <option value="estoque">Em Estoque</option>
          <option value="com_tecnico">Com Técnico</option>
          <option value="manutencao">Manutenção</option>
        </select>
        <select className="select w-48" value={techFilter} onChange={(e) => setTechFilter(e.target.value)} data-testid="tools-tech-filter">
          <option value="">Todos técnicos</option>
          {techs.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </div>

      <div className="card">
        <table className="data">
          <thead>
            <tr><th>Ferramenta</th><th>SN</th><th>Marca / Categoria</th><th>Status</th><th>Com técnico</th><th>Desde</th><th>Ações</th></tr>
          </thead>
          <tbody>
            {filtered.map((t) => {
              const s = STATUS[t.status] || { label: t.status, cls: "badge-open" };
              return (
                <tr key={t.id} data-testid={`tool-row-${t.serial_number}`}>
                  <td>
                    <div className="flex items-center gap-2"><Wrench size={13} style={{ color: "var(--text-mute)" }} />{t.name}</div>
                    {t.description && <div className="text-xs mt-1" style={{ color: "var(--text-mute)" }}>{t.description}</div>}
                  </td>
                  <td className="mono font-semibold">{t.serial_number}</td>
                  <td className="text-xs">{[t.brand, t.category].filter(Boolean).join(" · ") || "-"}</td>
                  <td><span className={`badge ${s.cls}`}>{s.label}</span></td>
                  <td>{t.technician_name || "-"}</td>
                  <td className="text-xs" style={{ color: "var(--text-dim)" }}>{t.assigned_at ? formatDate(t.assigned_at) : "-"}</td>
                  <td>
                    <div className="flex gap-1 flex-wrap">
                      {t.status === "estoque" && (
                        <button className="btn btn-ghost" onClick={() => openAssign(t)} data-testid={`assign-tool-${t.serial_number}`}><Send size={13} /> Atribuir</button>
                      )}
                      {t.status === "com_tecnico" && (
                        <>
                          <button className="btn btn-ghost" onClick={() => openAssign(t)} data-testid={`transfer-tool-${t.serial_number}`}><Send size={13} /> Transferir</button>
                          <button className="btn btn-ghost" onClick={() => returnTool(t)} data-testid={`return-tool-${t.serial_number}`}><Undo2 size={13} /> Devolver</button>
                          <button className="btn btn-ghost" onClick={() => printReceipt(t, "retirada")} title="Recibo de retirada" data-testid={`receipt-tool-${t.serial_number}`}><Printer size={13} /></button>
                        </>
                      )}
                      <button className="btn btn-ghost" onClick={() => openHistory(t)} data-testid={`history-tool-${t.serial_number}`}><History size={13} /></button>
                      <button className="btn btn-ghost" onClick={() => openEdit(t)}><Pencil size={13} /></button>
                      <button className="btn btn-ghost" onClick={() => remove(t)}><Trash2 size={13} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-sm" style={{ color: "var(--text-mute)" }}>Nenhuma ferramenta cadastrada.</td></tr>}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Editar Ferramenta" : "Nova Ferramenta"} testid="tool-modal"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setOpen(false)}>Cancelar</button>
          <button className="btn btn-primary" onClick={save} data-testid="save-tool-btn">Salvar</button>
        </>}>
        {err && <div className="mb-3 p-2 text-sm border" style={{ background: "rgba(239,68,68,0.08)", borderColor: "rgba(239,68,68,0.3)", color: "#FCA5A5" }}>{err}</div>}
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><label className="label-caps block mb-1">Nome</label><input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex.: Furadeira Bosch GSB 550" data-testid="tool-name" /></div>
          <div><label className="label-caps block mb-1">Nº de Série</label><input className="input mono" value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} data-testid="tool-sn" /></div>
          <div><label className="label-caps block mb-1">Marca</label><input className="input" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} data-testid="tool-brand" /></div>
          <div className="col-span-2"><label className="label-caps block mb-1">Categoria</label><input className="input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Elétrica, Manual, Medição..." data-testid="tool-category" /></div>
          <div className="col-span-2"><label className="label-caps block mb-1">Descrição</label><textarea className="textarea" rows="2" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
        </div>
      </Modal>

      <Modal open={assignOpen} onClose={() => setAssignOpen(false)} title="Atribuir Ferramenta a Técnico" testid="assign-tool-modal"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setAssignOpen(false)}>Cancelar</button>
          <button className="btn btn-primary" onClick={doAssign} data-testid="confirm-assign-tool">Atribuir</button>
        </>}>
        {assignErr && <div className="mb-3 p-2 text-sm border" style={{ background: "rgba(239,68,68,0.08)", borderColor: "rgba(239,68,68,0.3)", color: "#FCA5A5" }}>{assignErr}</div>}
        {assignForm.tool && (
          <>
            <div className="mb-3 p-3 border" style={{ borderColor: "var(--border-strong)" }}>
              <div className="label-caps mb-1">Ferramenta</div>
              <div className="text-sm">{assignForm.tool.name}</div>
              <div className="text-xs mono mt-1" style={{ color: "var(--text-mute)" }}>SN: <b style={{ color: "var(--text)" }}>{assignForm.tool.serial_number}</b></div>
              {assignForm.tool.technician_name && <div className="text-xs mt-1" style={{ color: "#FACC15" }}>Atualmente com: {assignForm.tool.technician_name}</div>}
            </div>
            <label className="label-caps block mb-1">Técnico</label>
            <select className="select mb-3" value={assignForm.technician_id} onChange={(e) => setAssignForm({ ...assignForm, technician_id: e.target.value })} data-testid="assign-tool-tech">
              <option value="">Selecione</option>
              {techs.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <label className="label-caps block mb-1">Observações (opcional)</label>
            <input className="input mb-4" value={assignForm.notes} onChange={(e) => setAssignForm({ ...assignForm, notes: e.target.value })} placeholder="Motivo, prazo estimado..." data-testid="assign-tool-notes" />
            <SignaturePad
              value={assignForm.signature}
              onChange={(sig) => setAssignForm({ ...assignForm, signature: sig })}
              testid="tool-signature"
            />
            <div className="mt-4">
              <PhotoUploader
                value={assignForm.photos}
                onChange={(photos) => setAssignForm({ ...assignForm, photos })}
                testid="tool-photos"
              />
            </div>
          </>
        )}
      </Modal>

      <Modal open={returnOpen} onClose={() => setReturnOpen(false)} title="Devolver Ferramenta" testid="return-tool-modal"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setReturnOpen(false)}>Cancelar</button>
          <button className="btn btn-primary" onClick={doReturn} data-testid="confirm-return-tool">Confirmar Devolução</button>
        </>}>
        {returnErr && <div className="mb-3 p-2 text-sm border" style={{ background: "rgba(239,68,68,0.08)", borderColor: "rgba(239,68,68,0.3)", color: "#FCA5A5" }}>{returnErr}</div>}
        {returnForm.tool && (
          <>
            <div className="mb-3 p-3 border" style={{ borderColor: "var(--border-strong)" }}>
              <div className="label-caps mb-1">Ferramenta</div>
              <div className="text-sm">{returnForm.tool.name}</div>
              <div className="text-xs mono mt-1" style={{ color: "var(--text-mute)" }}>SN: <b style={{ color: "var(--text)" }}>{returnForm.tool.serial_number}</b> · Estava com: <b style={{ color: "var(--text)" }}>{returnForm.tool.technician_name}</b></div>
            </div>
            <label className="label-caps block mb-1">Observações da devolução</label>
            <input className="input mb-4" value={returnForm.notes} onChange={(e) => setReturnForm({ ...returnForm, notes: e.target.value })} placeholder="Estado do item, ocorrências..." data-testid="return-tool-notes" />
            <SignaturePad
              value={returnForm.signature}
              onChange={(sig) => setReturnForm({ ...returnForm, signature: sig })}
              testid="return-tool-signature"
            />
            <div className="mt-4">
              <PhotoUploader
                value={returnForm.photos}
                onChange={(photos) => setReturnForm({ ...returnForm, photos })}
                testid="return-tool-photos"
              />
            </div>
          </>
        )}
      </Modal>

      <Modal open={histOpen} onClose={() => setHistOpen(false)} title={`Histórico · ${histTool?.name || ""}`} testid="tool-history-modal">
        {histItems.length === 0 ? (
          <div className="text-center py-8 text-sm" style={{ color: "var(--text-mute)" }}>Sem histórico ainda.</div>
        ) : (
          <div className="space-y-4">
            {histItems.map((h) => (
              <div key={h.id} className="p-4 border" style={{ borderColor: "var(--border)" }}>
                <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
                  <div>
                    <div className="font-semibold" style={{ fontFamily: "Chivo" }}>{h.technician_name}</div>
                    <div className="text-xs mt-1" style={{ color: "var(--text-mute)" }}>
                      Retirada: {formatDate(h.assigned_at)} · Devolução: {formatDate(h.returned_at)}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button className="btn btn-secondary" onClick={() => printHistoryReceipt(histTool, h, "retirada")}><Printer size={13} /> Retirada</button>
                    <button className="btn btn-secondary" onClick={() => printHistoryReceipt(histTool, h, "devolucao")}><Printer size={13} /> Devolução</button>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <ProofBlock label="Retirada" signature={h.pickup_signature} photos={h.pickup_photos} notes={h.notes} />
                  <ProofBlock label="Devolução" signature={h.return_signature} photos={h.return_photos} notes={h.return_notes} />
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
}

function ProofBlock({ label, signature, photos, notes }) {
  return (
    <div className="p-3 border" style={{ borderColor: "var(--border-strong)", background: "#0B1A2C" }}>
      <div className="label-caps mb-2">{label}</div>
      {signature ? (
        <div className="border mb-2 p-1 bg-white" style={{ borderColor: "var(--border-strong)" }}>
          <img src={signature} alt="assinatura" style={{ maxHeight: 80, width: "100%", objectFit: "contain", filter: "invert(1)" }} />
        </div>
      ) : (
        <div className="text-xs italic mb-2" style={{ color: "var(--text-mute)" }}>Sem assinatura</div>
      )}
      {photos && photos.length > 0 ? (
        <div className="grid grid-cols-3 gap-1 mb-2">
          {photos.map((p, i) => (
            <a key={i} href={p} target="_blank" rel="noreferrer">
              <img src={p} alt={`foto ${i}`} className="w-full h-16 object-cover border" style={{ borderColor: "var(--border-strong)" }} />
            </a>
          ))}
        </div>
      ) : (
        <div className="text-xs italic" style={{ color: "var(--text-mute)" }}>Sem fotos</div>
      )}
      {notes && <div className="text-xs mt-1" style={{ color: "var(--text-dim)" }}>{notes}</div>}
    </div>
  );
}
